import { set } from "lodash";
import { createContext, useContext, FC, useRef, useMemo } from "react";


export interface SubdataAssembleContextProps<T = {[k in string]: any}> {
  registerAssembleFn: (x: keyof T, assembleFn: ()=>Promise<T[keyof T]|AssembleIssue>) => () => void;
  inform: (x: keyof T, value: T[keyof T]|AssembleIssue) => void;
}

const SubdataAssembleContext = createContext<SubdataAssembleContextProps>({} as any)

export function useSubdataAssembleContext<T = {[k in string]: any}>() { 
  return useContext(SubdataAssembleContext) as SubdataAssembleContextProps<T>
}

export const SubdataAssembleContextProvider : FC<{value: SubdataAssembleContextProps}> = ({value, children}) => {
  return (
    <SubdataAssembleContext.Provider value={value}>
      {children}
    </SubdataAssembleContext.Provider>
  )
}

export function useSubdataAssembleController<T = {[k in string]: any}>(x: {
  getInformed: (x: keyof T, value: T[keyof T]|AssembleIssue) => void;
}): { 
  assemble: (excludes?: string[]) => Promise<Partial<T>|AssembleIssue>,
  contextValue: SubdataAssembleContextProps<T>, 
} {
  const getInformedRef = useRef(x.getInformed)
  const registryRef = useRef({} as any)

  return useMemo(() => {
    const registerAssembleFn = (key: keyof T, assembleFn: ()=>Promise<T[keyof T]|AssembleIssue>) => {
      registryRef.current[key] = assembleFn
      return () => {
        delete registryRef.current[key]
      }
    }
    const inform = (key: keyof T, value: T[keyof T]|AssembleIssue) => {
      getInformedRef.current(key, value)
    }
    const assemble = async (excludes?: string[]) => {
      const keys = Object.keys(registryRef.current)
      let key = keys.pop()
      const assembled = {} as any
      const errors = {} as any
      let hasError = false
      while (!!key) {
        if (!excludes || excludes.indexOf(key) === -1) {
          const assembleFn : () => Promise<T[keyof T]|AssembleIssue> = registryRef.current[key]
          const r = await assembleFn()
          if (r instanceof AssembleIssue) {
            set(errors, key, r.error)
            hasError = true
          } else {
            set(assembled, key, r)
          }
        }
        key = keys.pop()
      }
      return hasError ? new AssembleIssue(errors) : assembled
    }
    return { assemble, contextValue: {registerAssembleFn, inform} }
  }, [])
}

export class AssembleIssue {
  readonly error: any = ''
  constructor(err: any) {
    this.error = err
  }
}